import api from "../../../core/api";
import { Page } from "../../../shared/models/models";
import {
  ActivePartner,
  CreatePartnerInvitation,
  PartnerInvitation,
  PartnerInvitationsQuery,
  PartnerRelationship,
  PartnershipsQuery,
  PartnershipStats,
} from "../models/models";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.customersApiBaseUrl };
};

class PartnershipsApi {
  // ─── Invitations ─────────────────────────────────────────────

  sendInvitation(data: CreatePartnerInvitation): Promise<PartnerInvitation> {
    return api
      .post<PartnerInvitation>("/v1/partnerships/invitations", data, getRequestConfig())
      .then((x) => x.data);
  }

  getSentInvitations(query: PartnerInvitationsQuery): Promise<Page<PartnerInvitation>> {
    return api
      .get<Page<PartnerInvitation>>("/v1/partnerships/invitations/sent", {
        ...getRequestConfig(),
        params: query,
        paramsSerializer: { indexes: null },
      })
      .then((x) => x.data);
  }

  getReceivedInvitations(query: PartnerInvitationsQuery): Promise<Page<PartnerInvitation>> {
    return api
      .get<Page<PartnerInvitation>>("/v1/partnerships/invitations/received", {
        ...getRequestConfig(),
        params: query,
        paramsSerializer: { indexes: null },
      })
      .then((x) => x.data);
  }

  getInvitation(id: string): Promise<PartnerInvitation> {
    return api
      .get<PartnerInvitation>(`/v1/partnerships/invitations/${id}`, getRequestConfig())
      .then((x) => x.data);
  }

  acceptInvitation(id: string): Promise<void> {
    return api.post(`/v1/partnerships/invitations/${id}/accept`, null, getRequestConfig());
  }

  declineInvitation(id: string): Promise<void> {
    return api.post(`/v1/partnerships/invitations/${id}/decline`, null, getRequestConfig());
  }

  cancelInvitation(id: string): Promise<void> {
    return api.post(`/v1/partnerships/invitations/${id}/cancel`, null, getRequestConfig());
  }

  resendInvitation(id: string): Promise<void> {
    return api.post(`/v1/partnerships/invitations/${id}/resend`, null, getRequestConfig());
  }

  // ─── Partnerships ────────────────────────────────────────────

  getPartnerships(query: PartnershipsQuery): Promise<Page<PartnerRelationship>> {
    return api
      .get<Page<PartnerRelationship>>("/v1/partnerships", {
        ...getRequestConfig(),
        params: query,
        paramsSerializer: { indexes: null },
      })
      .then((x) => x.data);
  }

  getPartnership(id: string): Promise<PartnerRelationship> {
    return api
      .get<PartnerRelationship>(`/v1/partnerships/${id}`, getRequestConfig())
      .then((x) => x.data);
  }

  getStats(): Promise<PartnershipStats> {
    return api
      .get<PartnershipStats>("/v1/partnerships/stats", getRequestConfig())
      .then((x) => x.data);
  }

  /** Own active partners (JWT-derived tenant) — replaces the deleted public partners/{customerId} route. */
  getActivePartners(): Promise<ActivePartner[]> {
    return api
      .get<ActivePartner[]>("/v1/partnerships/partners", getRequestConfig())
      .then((x) => x.data);
  }

  getPartnershipWith(customerId: string): Promise<PartnerRelationship> {
    return api
      .get<PartnerRelationship>(`/v1/partnerships/with/${customerId}`, getRequestConfig())
      .then((x) => x.data);
  }

  deactivatePartnership(id: string): Promise<PartnerRelationship> {
    return api
      .delete<PartnerRelationship>(`/v1/partnerships/${id}`, getRequestConfig())
      .then((x) => x.data);
  }
}

export default new PartnershipsApi();
